import { useEffect, useRef, useState } from "react";
import useAuth from "@/hooks/useAuth";
import useSocket from "@/hooks/useSocket";
import useStore from "@/hooks/useStore";
import type { TyOrder } from "@/types";

// --- Types --------------------------------------------------------------------------------------

type Params = {
    /** Whether this screen wants to hear anything at all. Off leaves the rooms entirely. */
    isLive: boolean;
    /** An order that was not on the screen when it opened. Never put in the list by this hook. */
    onArrived: (order: TyOrder) => void;
    /** An order that may already be on the screen. Patched in place if it is, ignored if it is not. */
    onPatched: (order: TyOrder) => void;
    /**
     * The connection came back after having been up once. Whatever happened while it was down was
     * missed and cannot be counted, so the screen is told to say so rather than to claim nothing
     * arrived.
     */
    onResumed: () => void;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * The orders screen's one way to hear about orders — the socket's answer to `useFetch` being the
 * only way to reach the API.
 *
 * Joining is asked for, not given: the server puts the socket in the store's room when this mounts
 * and takes it out when it leaves, so a manager who is not looking at orders is not told about them,
 * and the one who is keeps the list he opened until he asks for more.
 *
 * The handlers are held in a ref, so a screen re-rendering with a new closure does not re-register
 * listeners or re-join rooms.
 */
export default function useOrderEvents({ isLive, onArrived, onPatched, onResumed }: Params) {
    const token = useAuth((state) => state.token);
    const storeId = useStore((state) => state.storeId);
    const sync = useSocket((state) => state.sync);
    const isConnected = useSocket((state) => state.isConnected);

    const [isWatching, setIsWatching] = useState(false);

    const handlers = useRef({ onArrived, onPatched, onResumed });
    // Kept current after each render rather than during it, so a screen re-rendering with new
    // closures neither re-registers listeners nor re-joins rooms.
    useEffect(() => {
        handlers.current = { onArrived, onPatched, onResumed };
    });

    /** Whether this socket has ever been up, so the first connect is not reported as a reconnect. */
    const hasConnected = useRef(false);

    useEffect(() => {
        if (!isLive) {
            setIsWatching(false);
            const open = useSocket.getState().socket;
            open?.emit("orders:unwatch");
            return;
        }

        const socket = sync(token, storeId);
        if (!socket) return;

        const handleCreated = (payload: { order: TyOrder }) => payload?.order && handlers.current.onArrived(payload.order);
        const handleUpdated = (payload: { order: TyOrder }) => payload?.order && handlers.current.onPatched(payload.order);
        const handleWatching = (watching: boolean) => setIsWatching(!!watching);
        /* Asked for again on every connect, not only on mount: a dropped connection loses its rooms,
           and the server will not remember a socket it no longer has. */
        const handleConnect = () => {
            socket.emit("orders:watch");
            if (hasConnected.current) handlers.current.onResumed();
            hasConnected.current = true;
        };

        socket.on("order:created", handleCreated);
        socket.on("order:updated", handleUpdated);
        socket.on("order:status", handleUpdated);
        socket.on("orders:watching", handleWatching);
        socket.on("connect", handleConnect);

        if (socket.connected) {
            socket.emit("orders:watch");
            hasConnected.current = true;
        }

        return () => {
            socket.emit("orders:unwatch");
            socket.off("order:created", handleCreated);
            socket.off("order:updated", handleUpdated);
            socket.off("order:status", handleUpdated);
            socket.off("orders:watching", handleWatching);
            socket.off("connect", handleConnect);
            setIsWatching(false);
        };
    }, [isLive, token, storeId, sync]);

    return { isConnected, isWatching };
}
